import { ActivityType } from '../../components/ActivityInfo/ActivityInfo';
import { activityKey, branchingKey, breatheKey, breatheStackKey, contentStackKey, genericKey, libraryStackKey, privateKey, sessionStackKey } from '../../constants/routeKeys';
import { concatPath } from '../../utils/links/concatPath';
import { contentTypeForActivityItem } from '../../types/types';
import { fetchedContentType, useFetchedContent } from './useFetchedContent';
import { logAmplitude } from '../../utils/analytics/analytics';
import { router } from 'expo-router';
import { useGoToFaceScan } from './faceScan/useGoToFaceScan';
import { useStores } from '../useStores';

export const useContentInfo = (contentId: string, type: contentTypeForActivityItem) => {
  const rootStore = useStores();
  const { goToFaceScan } = useGoToFaceScan();
  const data  = rootStore?.categories?.getCategoryItemFromContentId(contentId);
  const {data: fetchedContent} = useFetchedContent(contentId, type as Extract<contentTypeForActivityItem, fetchedContentType>);
  const title = data?.title || (fetchedContent as any)?.title;
  const color = data?.designSystemColor || (fetchedContent as any)?.designSystemColor || 'ELECTRIC_BLUE';
  const image = data?.image || (fetchedContent as any)?.image;
  let activityType: ActivityType = data?.activityType as ActivityType;

  let onPress = () => {};

  const params = {
    id: contentId,
  };

  /** every flow is a session: the wrapper session that plays this library item */
  const wrapperSessionId = (): string | undefined =>
    (data as any)?.sessionId || (fetchedContent as any)?.session?._id;

  const goToWrapperSession = (sessionId: string) => {
    router.navigate({
      pathname: concatPath([privateKey, sessionStackKey, branchingKey], -1) as string,
      params: {
        sessionId,
        reckoningId: null,
        fromPath: encodeURIComponent(false),
        fromReckoning: encodeURIComponent(false),
      },
    });
  };

  const goToBreathe = () => {
    router.navigate(concatPath([privateKey, libraryStackKey, breatheStackKey, breatheKey], -1) as string);
  };

  /**
   * Surveys and contents are played, never shown on a screen of their own:
   * the session that plays them is the only way in.
   */
  const goToPlayableSession = (activityTypeForLog?: string) => () => {
    // only where it was logged before: opening a content never raised an
    // event, and starting to do so would quietly change the analytics
    if (activityTypeForLog) {
      logAmplitude('Activity Started', {
        activityId: data?.humanReadableId,
        activityType: activityTypeForLog,
        from: 'library',
      });
    }
    const sessionId = wrapperSessionId();
    if (!sessionId) {
      // created with the entity and backfilled by migration 052, so this is a
      // broken record rather than a case to fall back on
      console.warn('No session plays library item', contentId);
      return;
    }
    goToWrapperSession(sessionId);
  };

  const goToGenericPage = () => {
    router.navigate({
      pathname: concatPath([libraryStackKey, genericKey], 2) as string,
      params,
    });
  };

  const goToActivity = () => {
    logAmplitude('Activity Opened', {
      activityId: data?.humanReadableId,
      from: 'library',
    });
    router.navigate({
      pathname: concatPath([privateKey, libraryStackKey, contentStackKey, activityKey], -1) as string,
      params,
    });
  };

  const activityTypeFromActivity = (activity: any): ActivityType => {
    switch (activity?.type) {
    case 'audio':
      return ActivityType.MEDITATION;
    case 'memo':
      return ActivityType.TOOLING;
    case 'journaling':
    default:
      return ActivityType.JOURNALING;
    }
  };

  switch (type) {
  case 'CONTENT_TEXT':
  case 'CONTENT_AUDIO':
  case 'CONTENT_VIDEO':
    onPress = goToPlayableSession();
    activityType = (fetchedContent as any)?.activityType as ActivityType || ActivityType.LESSON;
    break;
  case 'BREATHING':
    onPress = () => {
      rootStore?.technique.setTechniqueId(contentId);
      goToBreathe();
    };
    activityType = ActivityType.BREATHING;
    break;
  case 'SURVEY':
    onPress = goToPlayableSession('self_assessment');
    activityType = ActivityType.SELF_ASSESSMENT;
    break;
  case 'CATEGORY':
    onPress = goToGenericPage;
    activityType = ActivityType.LESSON;
    break;
  case 'FACE_SCAN':
    onPress = goToFaceScan;
    break;
  case 'ACTIVITY':
    onPress = goToActivity;
    activityType = activityTypeFromActivity(fetchedContent);
    break;
  }

  return { activityType, onPress, title, color, image };
};
